import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '../lib/errorReporting';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError({
      message: error.message || 'React render error',
      stack: error.stack,
      componentStack: info.componentStack || undefined,
      severity: 'error',
      source: 'react-error-boundary'
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Noe gikk galt
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            Vi har automatisk varslet driften. Du kan prøve å laste siden på nytt.
          </p>
          {this.state.errorMessage && (
            <details className="text-left mb-4">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                Tekniske detaljer
              </summary>
              <pre className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-700 overflow-auto max-h-32 whitespace-pre-wrap">
                {this.state.errorMessage}
              </pre>
            </details>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Last siden på nytt
          </button>
        </div>
      </div>
    );
  }
}
